import {
  memo,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { type CustomCellEditorProps } from "ag-grid-react";

import { type ThemedDropdownOptionType } from "@vega-core/components/Form/Controls/Dropdown/ThemedDropdown/ThemedDropdown";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@vega-core/components/shadcn/popover";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";

type DropdownCellEditorProps = CustomCellEditorProps<
  unknown,
  ThemedDropdownOptionType["value"]
> & {
  options: ThemedDropdownOptionType[];
  renderOption?: (option: ThemedDropdownOptionType) => ReactNode;
};

export const DropdownCellEditor = memo(
  ({
    initialValue,
    onValueChange,
    stopEditing,
    options,
    renderOption,
  }: DropdownCellEditorProps) => {
    const refContainer = useRef<HTMLDivElement>(null);
    const [hoveredOptionValue, setHoveredOptionValue] = useState(initialValue);
    const [ready, setReady] = useState(false);

    const handleKeyDown = useCallback(
      (event: KeyboardEvent) => {
        if (!ready) return;

        if (event.key === "Enter") {
          onValueChange(hoveredOptionValue);
          stopEditing();

          return;
        }

        if (!["ArrowUp", "ArrowDown"].includes(event.key)) return;

        event.preventDefault();

        const currentIndex = options.findIndex(
          (o) => o.value === hoveredOptionValue,
        );
        const isDown = event.key === "ArrowDown";
        const nextIndex = isDown
          ? Math.min(currentIndex + 1, options.length - 1)
          : Math.max(currentIndex - 1, 0);

        setHoveredOptionValue(options[nextIndex]?.value);
      },
      [ready, options, hoveredOptionValue, onValueChange, stopEditing],
    );

    useEffect(() => {
      refContainer.current?.focus();
      setReady(true);
    }, []);

    useEffect(() => {
      window.addEventListener("keydown", handleKeyDown, true);

      return () => {
        window.removeEventListener("keydown", handleKeyDown, true);
      };
    }, [handleKeyDown]);

    return (
      <Popover open>
        <PopoverTrigger asChild>
          <div
            className="focus:outline-none h-full w-full"
            ref={refContainer}
            tabIndex={1}
          />
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="p-0.5 w-min-content"
          onOpenAutoFocus={(e) => e.preventDefault()}
          sideOffset={0}
        >
          {options.map((option) => (
            <div
              aria-selected={hoveredOptionValue === option.value}
              className={cnTwMerge(
                "relative flex w-full cursor-pointer items-center rounded-companyBorderRadius px-4 py-2 body-base-medium",
                {
                  "bg-background/component":
                    option.value === hoveredOptionValue,
                },
              )}
              key={option.value}
              onClick={() => {
                onValueChange(option.value);
                stopEditing();
              }}
              onMouseDown={(e) => {
                e.stopPropagation(); // keep focus on the editor's trigger
              }}
              onMouseMove={() => {
                setHoveredOptionValue(option.value);
              }}
              role="option"
            >
              {renderOption ? renderOption(option) : option.label}
            </div>
          ))}
        </PopoverContent>
      </Popover>
    );
  },
);
